import Phaser from "phaser";
import caveVoidPng from "../../../@shared/assets/GameMaps/Void-Cave/FDR_Caves_VOID.png";
import cavePalettePng from "../../../@shared/assets/GameMaps/Void-Cave/cave-pallete.png";
import whisperingPropsPng from "../../../@shared/assets/GameMaps/tilesets/forest/TX Props.png";
import AudioManager from "../../../@shared/utils/AudioManager";
import {
  getAvatarSpriteConfig,
  scaleAvatarBodySize,
} from "../../../@shared/utils/SpritesMap";
import { createWalkAnimations } from "../../../@shared/utils/SceneHelpers";
import {
  createLocalOrbAvatarImage,
  getLocalOrbAvatarConfig,
  preloadLocalOrbAvatar,
  updateLocalOrbAvatar,
} from "../../../@shared/utils/phaserOrbAvatar";
import { applyWorldSceneBackground } from "../../../@shared/utils/worldSceneBackgrounds";
import {
  createStoryRuneStoneTeleportEffect,
  destroyStoryRuneStoneTeleportEffect,
  syncStoryRuneStoneTeleportEffect,
} from "../../../@shared/utils/storyRuneStoneTeleportEffect";
import {
  createCaveCreatureEncounters,
  destroyCaveCreatureEncounters,
  preloadCaveCreatureEncounters,
  updateCaveCreatureEncounters,
} from "./caveCreatureEncounterController";
import {
  VOID_CAVE_CHEST_ANIMATION_COMPLETE_EVENT,
  attachVoidCaveStoryChestEvents,
  clearVoidCaveStoryChestVisit,
  createVoidCaveChestBodies,
  detachVoidCaveStoryChestEvents,
  drawVoidCaveChestCollisionDebug,
  getOrCreateVoidCaveStoryChestVisit,
  getVoidCaveGroundEntityDepth,
  markVoidCaveStoryChestOpened,
  requestVoidCaveStoryChestOpen,
} from "./voidCaveStoryPackChests";
import {
  CAVE_FOREGROUND_LAYER_NAMES,
  drawDepthSortedCaveForegroundLayer,
} from "./caveDepthSorting";

const DOOR_SFX = require("../../../@shared/assets/sounds/door.mp3");
const TILE_SIZE = 32;
const PLAYER_DEPTH = 900;
const BRIDGE_PLAYER_DEPTH = 1300;
const COLLISION_LAYER_NAMES = ["CaveWall", "Holes", "Rocks"];
const CAVE_AMBIENT_PARTICLE_DEPTH = 1180;
const STORY_RUNE_STONE_TELEPORT_Y_OFFSET = 18;
const CHEST_TEXTURE_RECTS = {
  closed: { x: 96, y: 30, width: 32, height: 31 },
  open: { x: 96, y: 76, width: 32, height: 49 },
};

/**
 * Builds a Phaser scene class for a generated void-cave cavern.
 *
 * options:
 * - sceneKey: unique Phaser scene key
 * - mapConfig: from westPassageMapConfigs.buildCavernMapConfig
 * - entranceRoute: { sceneKey, spawn, doorLabel } for the way back
 * - entranceLabel: short text drawn over the entrance glow
 * - exitRoutes: { [exitName]: { sceneKey, spawn, doorLabel, sealedMessage } }
 *   (exit doorways are intentionally unlabelled in the world)
 * - storyMapId / storySpawnId: story mode world tracking
 */
export const createCavernScene = ({
  sceneKey,
  mapConfig,
  entranceRoute,
  entranceLabel,
  exitRoutes = {},
  storyMapId,
  storySpawnId,
}) => {
  const source = mapConfig.source;
  const MAP_WIDTH = source.width * source.tilewidth;
  const MAP_HEIGHT = source.height * source.tileheight;

  return class extends Phaser.Scene {
    constructor() {
      super({ key: sceneKey });
    }

    init(data = {}) {
      this.spawnPoint = data.spawn || mapConfig.entrySpawn;
      this.cityReturnSpawn = data.cityReturnSpawn || data.returnSpawn || null;
      this.heroUrl = data.heroUrl || null;
      this.heroAvatarKey = data.heroAvatarKey || "io_default";
      this.heroSpriteConfig = getAvatarSpriteConfig(this.heroAvatarKey);
      this.userData = data.userData || null;
      const { avatarData, textureKey } = getLocalOrbAvatarConfig(this.userData);
      this.localOrbAvatarData = avatarData;
      this.localOrbTextureKey = textureKey;
    }

    preload() {
      if (this.heroUrl && !this.textures.exists("hero")) {
        this.load.spritesheet("hero", this.heroUrl, {
          frameWidth: this.heroSpriteConfig.frameWidth,
          frameHeight: this.heroSpriteConfig.frameHeight,
        });
      }
      preloadLocalOrbAvatar(
        this,
        this.localOrbAvatarData,
        this.localOrbTextureKey,
      );
      preloadCaveCreatureEncounters(this);

      this.load.spritesheet("vcaveBase", caveVoidPng, {
        frameWidth: TILE_SIZE,
        frameHeight: TILE_SIZE,
      });
      this.load.spritesheet("vcavePalette", cavePalettePng, {
        frameWidth: TILE_SIZE,
        frameHeight: TILE_SIZE,
      });
      this.load.image("vcavePropsAtlas", whisperingPropsPng);

      AudioManager.loadSFX("door", DOOR_SFX);
    }

    create() {
      applyWorldSceneBackground(this);
      this.buildTilesetRegistry();
      this.ensureAtlasTextures();
      this.ensureAmbientParticleTextures();
      this.chestSprites = new Map();
      this.chestBodies = new Map();
      this.currentInteractable = null;
      this.sealedMessageText = null;
      this.storyRuneStoneTeleportEffect = null;
      this.storyRandomChestSpecs = getOrCreateVoidCaveStoryChestVisit({
        source,
        sceneKey,
        entrySpawn: this.spawnPoint,
      });
      this.drawMapFromSource();
      this.createCollisionBodiesFromLayers();
      this.storyRandomChestSpecs.forEach((chest) => this.createChest(chest));
      attachVoidCaveStoryChestEvents(this);
      this.createAmbientParticles();

      this.matter.world.setBounds(0, 0, MAP_WIDTH, MAP_HEIGHT);
      this.cameras.main.setBounds(0, 0, MAP_WIDTH, MAP_HEIGHT);

      this.player = this.matter.add
        .sprite(this.spawnPoint.x, this.spawnPoint.y, "hero", 0)
        .setBody(
          scaleAvatarBodySize(this.heroAvatarKey, { width: 64, height: 80 }),
        )
        .setScale(this.heroSpriteConfig.worldScale || 1)
        .setFixedRotation()
        .setDepth(this.getPlayerDepthAt(this.spawnPoint.x, this.spawnPoint.y));
      this.playerOrbImage = createLocalOrbAvatarImage(
        this,
        this.player,
        this.spawnPoint,
        this.localOrbAvatarData,
        this.localOrbTextureKey,
        { depth: this.player.depth },
      );
      createCaveCreatureEncounters(this, {
        source,
        storyMapId,
        reservedPoints: this.storyRandomChestSpecs,
      });

      createWalkAnimations(this, "hero", this.heroAvatarKey);

      const isMobile =
        this.sys.game.device.os.android || this.sys.game.device.os.iOS;
      this.cameras.main
        .startFollow(this.player, true, 0.1, 0.1)
        .setZoom(isMobile ? 1.1 : 1.65);

      this.createEntrancePortal();
      this.createExitDoors();

      this.currentDoor = null;
      this.joystick = { x: 0, y: 0 };
      this.cursors = this.input.keyboard.createCursorKeys();

      if (this.userData?.activeGameMode === "story") {
        window.dispatchEvent(
          new CustomEvent("story-world-location", {
            detail: {
              sceneKey: this.scene.key,
              world: {
                mapId: storyMapId,
                building: "",
                spawnId: storySpawnId,
                x: this.spawnPoint.x,
                y: this.spawnPoint.y,
              },
            },
          }),
        );
      }
      this.keys = this.input.keyboard.addKeys({
        W: Phaser.Input.Keyboard.KeyCodes.W,
        A: Phaser.Input.Keyboard.KeyCodes.A,
        S: Phaser.Input.Keyboard.KeyCodes.S,
        D: Phaser.Input.Keyboard.KeyCodes.D,
      });
      this.spaceKey = this.input.keyboard.addKey(
        Phaser.Input.Keyboard.KeyCodes.SPACE,
      );

      this.matter.world.on("collisionstart", (event) => {
        event.pairs.forEach(({ bodyA, bodyB }) => {
          const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
          const isPlayer =
            bodyA === this.player.body || bodyB === this.player.body;
          if (!sensor || !isPlayer) return;

          if (sensor.label === "entrance") {
            this.currentDoor = "entrance";
            this.currentInteractable = { type: "entrance" };
            this.setInteractPrompt(entranceRoute.doorLabel);
          } else if (sensor.label?.startsWith("exit:")) {
            const exitName = sensor.label.slice(5);
            const route = exitRoutes[exitName];
            if (!route) return;
            this.currentDoor = sensor.label;
            this.currentInteractable = { type: "exit", id: exitName };
            this.setInteractPrompt(route.doorLabel);
          } else if (sensor.label?.startsWith("chest:")) {
            const chestId = sensor.label.slice(6);
            const chest = this.chestSprites.get(chestId);
            if (!chest || chest.opened) return;
            this.currentInteractable = { type: "chest", id: chestId };
            this.setInteractPrompt("Open Chest");
          }
        });
      });

      this.matter.world.on("collisionend", (event) => {
        event.pairs.forEach(({ bodyA, bodyB }) => {
          const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
          const isPlayer =
            bodyA === this.player.body || bodyB === this.player.body;
          if (!sensor || !isPlayer) return;

          if (
            sensor.label === "entrance" ||
            sensor.label?.startsWith("exit:")
          ) {
            if (this.currentDoor === sensor.label || sensor.label === "entrance") {
              this.currentDoor = null;
            }
            if (
              this.currentInteractable?.type === "entrance" ||
              this.currentInteractable?.type === "exit"
            ) {
              this.currentInteractable = null;
              this.clearInteractPrompt();
            }
          } else if (
            sensor.label?.startsWith("chest:")
            && this.currentInteractable?.type === "chest"
            && this.currentInteractable.id === sensor.label.slice(6)
          ) {
            this.currentInteractable = null;
            this.clearInteractPrompt();
          }
        });
      });

      this._onEnter = () => {
        if (this.currentInteractable?.type === "chest") {
          this.requestOpenChest(this.currentInteractable.id);
          return;
        }
        if (this.currentInteractable?.type === "exit") {
          const route = exitRoutes[this.currentInteractable.id];
          if (!route) return;
          if (!route.sceneKey) {
            this.showSealedMessage(route.sealedMessage);
            return;
          }
          this.travelTo(route.sceneKey, route.spawn);
          return;
        }
        if (this.currentInteractable?.type !== "entrance") return;
        this.travelTo(entranceRoute.sceneKey, entranceRoute.spawn);
      };

      this.spaceKey.on("down", this._onEnter);
      window.addEventListener("press-enter", this._onEnter);
      this._onStoryRuneStoneTeleport = (event) => {
        const targetSceneKey = String(event?.detail?.sceneKey || "");
        if (targetSceneKey && targetSceneKey !== this.scene.key) return;
        if (this.userData?.activeGameMode !== "story") return;
        this.playStoryRuneStoneTeleportEffect(
          String(event?.detail?.effectId || ""),
        );
      };
      window.addEventListener(
        "story-rune-stone-teleport",
        this._onStoryRuneStoneTeleport,
      );
      this.events.once("shutdown", () => {
        this.spaceKey?.off?.("down", this._onEnter);
        window.removeEventListener("press-enter", this._onEnter);
        window.removeEventListener(
          "story-rune-stone-teleport",
          this._onStoryRuneStoneTeleport,
        );
        this.destroyStoryRuneStoneTeleportEffect();
        detachVoidCaveStoryChestEvents(this);
        destroyCaveCreatureEncounters(this);
        window.dispatchEvent(
          new CustomEvent("interact-proximity", { detail: false }),
        );
        window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
      });

      this.cameras.main.fadeIn(300, 0, 0, 0);
    }

    travelTo(targetSceneKey, spawn) {
      if (!targetSceneKey) return;
      AudioManager.playSFX("door");
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: false }),
      );
      window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
      this.currentDoor = null;
      this.currentInteractable = null;
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once("camerafadeoutcomplete", () => {
        clearVoidCaveStoryChestVisit(this.scene.key);
        this.scene.start(targetSceneKey, {
          spawn,
          cityReturnSpawn: this.cityReturnSpawn,
          heroUrl: this.heroUrl,
          heroAvatarKey: this.heroAvatarKey,
          userData: this.userData,
        });
      });
    }

    showSealedMessage(message) {
      if (!message) return;
      if (this.sealedMessageText) {
        this.sealedMessageText.destroy();
        this.sealedMessageText = null;
      }

      const text = this.add
        .text(this.player.x, this.player.y - 70, message, {
          font: "15px Arial",
          fill: "#e9e2ff",
          stroke: "#352554",
          strokeThickness: 4,
          align: "center",
          wordWrap: { width: 220, useAdvancedWrap: true },
        })
        .setOrigin(0.5, 1)
        .setDepth(4100);
      this.sealedMessageText = text;

      this.tweens.add({
        targets: text,
        y: text.y - 16,
        alpha: { from: 1, to: 0 },
        delay: 1400,
        duration: 700,
        onComplete: () => {
          if (this.sealedMessageText === text) this.sealedMessageText = null;
          text.destroy();
        },
      });
    }

    emitStoryRuneStoneTeleportComplete(effectId = "") {
      if (typeof window === "undefined" || !effectId) return;

      window.dispatchEvent(
        new CustomEvent("story-rune-stone-teleport-complete", {
          detail: {
            effectId,
            sceneKey: this.scene.key,
          },
        }),
      );
    }

    getStoryRuneStoneTeleportAnchor() {
      const position = this.player?.body?.position;
      if (
        position &&
        Number.isFinite(position.x) &&
        Number.isFinite(position.y)
      ) {
        return {
          x: position.x,
          y: position.y,
        };
      }

      return null;
    }

    playStoryRuneStoneTeleportEffect(effectId = "") {
      if (!effectId) return;

      this.destroyStoryRuneStoneTeleportEffect();

      const anchor = this.getStoryRuneStoneTeleportAnchor();
      if (!anchor) {
        this.emitStoryRuneStoneTeleportComplete(effectId);
        return;
      }

      this.storyRuneStoneTeleportEffect = createStoryRuneStoneTeleportEffect(
        this,
        {
          x: anchor.x,
          y: anchor.y + STORY_RUNE_STONE_TELEPORT_Y_OFFSET,
          depth: (this.player?.depth || PLAYER_DEPTH) + 120,
          target: this.player,
          onComplete: () => {
            this.storyRuneStoneTeleportEffect = null;
            this.emitStoryRuneStoneTeleportComplete(effectId);
          },
        },
      );
    }

    syncStoryRuneStoneTeleportEffect() {
      const anchor = this.getStoryRuneStoneTeleportAnchor();
      if (!anchor || !this.storyRuneStoneTeleportEffect) return;

      syncStoryRuneStoneTeleportEffect(this.storyRuneStoneTeleportEffect, {
        x: anchor.x,
        y: anchor.y + STORY_RUNE_STONE_TELEPORT_Y_OFFSET,
        depth: (this.player?.depth || PLAYER_DEPTH) + 120,
      });
    }

    destroyStoryRuneStoneTeleportEffect() {
      destroyStoryRuneStoneTeleportEffect(this.storyRuneStoneTeleportEffect);
      this.storyRuneStoneTeleportEffect = null;
    }

    buildTilesetRegistry() {
      const textureKeyByBasename = {
        "FDR_Caves_VOID.tsx": "vcaveBase",
        "cave-pallete.tsx": "vcavePalette",
      };

      this.tilesetRegistry = [...(source?.tilesets || [])]
        .map((tileset) => ({
          ...tileset,
          textureKey:
            textureKeyByBasename[
              tileset.basename || tileset.source?.split("/").pop()
            ],
        }))
        .filter((tileset) => tileset.textureKey)
        .sort((a, b) => a.firstgid - b.firstgid);
    }

    resolveTilesetForGid(gid) {
      if (!gid) return null;

      let found = null;
      this.tilesetRegistry.forEach((tileset) => {
        if (tileset.firstgid <= gid) found = tileset;
      });

      if (!found) return null;
      return {
        ...found,
        frame: gid - found.firstgid,
      };
    }

    getLayerByName(name) {
      return source?.layers?.find(
        (layer) => layer.type === "tilelayer" && layer.name === name,
      );
    }

    drawStaticLayerFromSource(layerName, depth) {
      const layer = this.getLayerByName(layerName);
      if (!layer) return;

      const rt = this.make.renderTexture({
        width: MAP_WIDTH,
        height: MAP_HEIGHT,
        add: false,
      });

      layer.data.forEach((gid, index) => {
        if (!gid) return;
        const resolved = this.resolveTilesetForGid(gid);
        if (!resolved) return;

        const tx = index % layer.width;
        const ty = Math.floor(index / layer.width);
        rt.drawFrame(
          resolved.textureKey,
          resolved.frame,
          tx * TILE_SIZE,
          ty * TILE_SIZE,
        );
      });

      const textureKey = `${sceneKey}-${layerName}`;
      if (this.textures.exists(textureKey)) {
        this.textures.remove(textureKey);
      }
      rt.saveTexture(textureKey);
      this.add.image(0, 0, textureKey).setOrigin(0).setDepth(depth);
    }

    drawMapFromSource() {
      this.drawStaticLayerFromSource("CaveGround", 1);
      this.drawStaticLayerFromSource("Ground", 2);
      this.drawStaticLayerFromSource("Holes", 3);
      CAVE_FOREGROUND_LAYER_NAMES.forEach((layerName) => {
        drawDepthSortedCaveForegroundLayer(this, {
          layerName,
          tileSize: TILE_SIZE,
        });
      });
    }

    createCollisionBodiesFromLayers() {
      COLLISION_LAYER_NAMES.forEach((name) => {
        const layer = this.getLayerByName(name);
        if (!layer) return;

        for (let ty = 0; ty < layer.height; ty += 1) {
          let runStart = null;
          for (let tx = 0; tx <= layer.width; tx += 1) {
            const gid =
              tx < layer.width ? layer.data[ty * layer.width + tx] : 0;
            const solid =
              Boolean(gid) && !mapConfig.isNavigationTileWalkable(tx, ty);

            if (solid && runStart === null) {
              runStart = tx;
            } else if (!solid && runStart !== null) {
              const runWidth = tx - runStart;
              this.matter.add.rectangle(
                runStart * TILE_SIZE + (runWidth * TILE_SIZE) / 2,
                ty * TILE_SIZE + TILE_SIZE / 2,
                runWidth * TILE_SIZE,
                TILE_SIZE,
                {
                  isStatic: true,
                  label: "terrain",
                },
              );
              runStart = null;
            }
          }
        }
      });
    }

    createEntrancePortal() {
      const rect = mapConfig.entranceRect;
      if (!rect) return;
      const centerX = rect.x + rect.width / 2;
      const centerY = rect.y + rect.height / 2;

      this.matter.add.rectangle(centerX, centerY, rect.width, rect.height, {
        isStatic: true,
        isSensor: true,
        label: "entrance",
      });

      // Cave-mouth glow, matching the void cave entrance in the whispering forest.
      const glow = this.add.graphics().setDepth(3000);
      glow.fillStyle(0x9f93ff, 0.18);
      glow.fillEllipse(centerX, centerY + 8, 180, 64);
      glow.fillStyle(0xb8a6ff, 0.42);
      glow.fillCircle(centerX, centerY, 28);
      glow.lineStyle(3, 0xf1ebff, 0.92);
      glow.strokeCircle(centerX, centerY, 18);

      if (entranceLabel) {
        this.add
          .text(centerX, centerY - 38, entranceLabel, {
            font: "16px Arial",
            fill: "#f7f3ff",
            stroke: "#352554",
            strokeThickness: 4,
          })
          .setOrigin(0.5)
          .setDepth(3001);
      }
    }

    createExitDoors() {
      Object.entries(mapConfig.exitRects).forEach(([exitName, rect]) => {
        if (!rect || !exitRoutes[exitName]) return;

        this.matter.add.rectangle(
          rect.x + rect.width / 2,
          rect.y + rect.height / 2,
          rect.width,
          rect.height,
          {
            isStatic: true,
            isSensor: true,
            label: `exit:${exitName}`,
          },
        );
      });
    }

    ensureAtlasTextures() {
      const propSource = this.textures.get("vcavePropsAtlas")?.getSourceImage();
      if (!propSource) return;

      Object.entries(CHEST_TEXTURE_RECTS).forEach(([kind, rect]) => {
        const key = `vcave-chest-${kind}`;
        if (this.textures.exists(key)) return;

        const canvasTexture = this.textures.createCanvas(
          key,
          rect.width,
          rect.height,
        );
        const ctx = canvasTexture.context;
        ctx.drawImage(
          propSource,
          rect.x,
          rect.y,
          rect.width,
          rect.height,
          0,
          0,
          rect.width,
          rect.height,
        );
        canvasTexture.refresh();
      });
    }

    ensureAmbientParticleTextures() {
      if (!this.textures.exists("vcaveAmbientGlow")) {
        const glowTexture = this.textures.createCanvas(
          "vcaveAmbientGlow",
          12,
          12,
        );
        const glowCtx = glowTexture.context;
        const gradient = glowCtx.createRadialGradient(6, 6, 1, 6, 6, 6);
        gradient.addColorStop(0, "rgba(210, 232, 255, 0.92)");
        gradient.addColorStop(0.45, "rgba(152, 171, 255, 0.55)");
        gradient.addColorStop(1, "rgba(152, 171, 255, 0)");
        glowCtx.fillStyle = gradient;
        glowCtx.fillRect(0, 0, 12, 12);
        glowTexture.refresh();
      }

      if (!this.textures.exists("vcaveAmbientDust")) {
        const dustTexture = this.textures.createCanvas(
          "vcaveAmbientDust",
          12,
          8,
        );
        const dustCtx = dustTexture.context;
        dustCtx.fillStyle = "rgba(178, 170, 148, 0.95)";
        dustCtx.beginPath();
        dustCtx.ellipse(6, 4, 4.6, 2.4, -0.2, 0, Math.PI * 2);
        dustCtx.fill();
        dustCtx.strokeStyle = "rgba(120, 110, 88, 0.7)";
        dustCtx.lineWidth = 1;
        dustCtx.beginPath();
        dustCtx.moveTo(3.2, 5.1);
        dustCtx.lineTo(8.5, 2.9);
        dustCtx.stroke();
        dustTexture.refresh();
      }

      if (!this.textures.exists("vcaveAmbientMist")) {
        const mistTexture = this.textures.createCanvas(
          "vcaveAmbientMist",
          18,
          18,
        );
        const mistCtx = mistTexture.context;
        const mistGradient = mistCtx.createRadialGradient(9, 9, 2, 9, 9, 9);
        mistGradient.addColorStop(0, "rgba(230, 236, 255, 0.66)");
        mistGradient.addColorStop(0.6, "rgba(180, 188, 228, 0.28)");
        mistGradient.addColorStop(1, "rgba(180, 188, 228, 0)");
        mistCtx.fillStyle = mistGradient;
        mistCtx.beginPath();
        mistCtx.arc(9, 9, 9, 0, Math.PI * 2);
        mistCtx.fill();
        mistTexture.refresh();
      }
    }

    createAmbientParticles() {
      const glowParticles = this.add.particles(0, 0, "vcaveAmbientGlow", {
        x: { min: 96, max: MAP_WIDTH - 96 },
        y: { min: 160, max: MAP_HEIGHT - 128 },
        lifespan: { min: 3000, max: 4600 },
        speedX: { min: -9, max: 9 },
        speedY: { min: -20, max: -8 },
        scale: { start: 0.7, end: 0.14 },
        alpha: { start: 0.34, end: 0 },
        blendMode: "ADD",
        frequency: 440,
        quantity: 2,
      });
      glowParticles.setDepth(CAVE_AMBIENT_PARTICLE_DEPTH);

      const dustParticles = this.add.particles(0, 0, "vcaveAmbientDust", {
        x: { min: 128, max: MAP_WIDTH - 128 },
        y: { min: 192, max: MAP_HEIGHT - 96 },
        lifespan: { min: 4600, max: 6800 },
        speedX: { min: -18, max: 14 },
        speedY: { min: 7, max: 15 },
        rotate: { min: -22, max: 22 },
        angularVelocity: { min: -12, max: 12 },
        scale: { start: 0.66, end: 0.38 },
        alpha: { start: 0.34, end: 0 },
        frequency: 720,
        quantity: 2,
      });
      dustParticles.setDepth(CAVE_AMBIENT_PARTICLE_DEPTH - 20);

      const mistPoints = [];
      if (mapConfig.entranceRect) {
        mistPoints.push({
          x: mapConfig.entranceRect.x + mapConfig.entranceRect.width / 2,
          y: mapConfig.entranceRect.y - 8,
        });
      }
      mapConfig.bridgeRects.forEach((rect) => {
        mistPoints.push({
          x: rect.x + rect.width / 2,
          y: rect.y + rect.height / 2,
        });
      });

      mistPoints.forEach((point) => {
        const mistParticles = this.add.particles(
          point.x,
          point.y,
          "vcaveAmbientMist",
          {
            lifespan: { min: 1800, max: 2600 },
            speedX: { min: -4, max: 4 },
            speedY: { min: -22, max: -12 },
            scale: { start: 0.52, end: 1.08 },
            alpha: { start: 0.22, end: 0 },
            rotate: { min: -8, max: 8 },
            frequency: 1150,
            quantity: 1,
          },
        );
        mistParticles.setDepth(CAVE_AMBIENT_PARTICLE_DEPTH - 10);
      });
    }

    createChest(chest) {
      const rect = CHEST_TEXTURE_RECTS.closed;
      const isOpened = Boolean(chest.opened);
      const spriteY = isOpened
        ? chest.y - (CHEST_TEXTURE_RECTS.open.height - rect.height)
        : chest.y;
      const sprite = this.add
        .image(
          chest.x,
          spriteY,
          isOpened ? "vcave-chest-open" : "vcave-chest-closed",
        )
        .setOrigin(0, 0)
        .setDepth(this.getGroundEntityDepthAt(chest.y + rect.height - 4));

      const { collisionBody, interactionBody } = createVoidCaveChestBodies(
        this,
        chest,
        rect,
        { isOpened },
      );
      const { collisionDebugGraphics, interactionDebugGraphics } =
        drawVoidCaveChestCollisionDebug(this, chest, rect, { isOpened });

      this.chestSprites.set(chest.id, {
        ...chest,
        sprite,
        collisionBody,
        collisionDebugGraphics,
        interactionDebugGraphics,
        opened: isOpened,
      });
      if (interactionBody) this.chestBodies.set(chest.id, interactionBody);
    }

    openChest(chestId) {
      const chest = this.chestSprites.get(chestId);
      const body = this.chestBodies.get(chestId);
      if (!chest || chest.opened) return;

      chest.sprite.setTexture("vcave-chest-open");
      chest.sprite.setY(
        chest.y -
          (CHEST_TEXTURE_RECTS.open.height - CHEST_TEXTURE_RECTS.closed.height),
      );
      chest.sprite.setDepth(
        this.getGroundEntityDepthAt(
          chest.y + CHEST_TEXTURE_RECTS.closed.height - 4,
        ),
      );
      chest.opened = true;
      markVoidCaveStoryChestOpened(this.scene.key, chestId);
      this.currentInteractable = null;
      this.clearInteractPrompt();

      if (body) {
        this.matter.world.remove(body);
        this.chestBodies.delete(chestId);
      }
      chest.interactionDebugGraphics?.destroy();
      chest.interactionDebugGraphics = null;

      this.tweens.add({
        targets: chest.sprite,
        scaleX: { from: 0.92, to: 1 },
        scaleY: { from: 0.92, to: 1 },
        y: { from: chest.sprite.y + 4, to: chest.sprite.y },
        duration: 180,
        ease: "Back.Out",
        onComplete: () => {
          window.dispatchEvent(
            new CustomEvent(VOID_CAVE_CHEST_ANIMATION_COMPLETE_EVENT, {
              detail: { chestId },
            }),
          );
        },
      });
    }

    requestOpenChest(chestId) {
      requestVoidCaveStoryChestOpen(this, chestId);
    }

    setInteractPrompt(label) {
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: true }),
      );
      window.dispatchEvent(new CustomEvent("door-name", { detail: label }));
    }

    clearInteractPrompt() {
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: false }),
      );
      window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
    }

    getGroundEntityDepthAt(y) {
      return getVoidCaveGroundEntityDepth(y, MAP_HEIGHT);
    }

    getPlayerDepthAt(x, y) {
      return mapConfig.isBridgePoint(x, y)
        ? BRIDGE_PLAYER_DEPTH
        : this.getGroundEntityDepthAt(y + 24);
    }

    update() {
      this.syncStoryRuneStoneTeleportEffect();
      updateCaveCreatureEncounters(this);

      const sceneInputLocked =
        typeof window !== "undefined" &&
        (
          Boolean(window.__storySceneInputLocked) ||
          Boolean(window.__storyWorldEncounterPaused)
        );

      if (sceneInputLocked) {
        const playerDepth = this.getPlayerDepthAt(this.player.x, this.player.y);
        this.player.setVelocity(0, 0);
        this.player.setDepth(playerDepth);
        this.player.anims?.stop?.();
        updateLocalOrbAvatar(
          this,
          this.player,
          this.playerOrbImage,
          this.localOrbAvatarData,
          {
            x: this.player.x,
            y: this.player.y,
            vx: 0,
            depth: playerDepth,
          },
        );
        return;
      }

      const speed = 2;
      let vx = 0;
      let vy = 0;
      const { x: jx, y: jy } = this.joystick;

      if (jx || jy) {
        vx = jx * speed;
        vy = jy * speed;
      } else {
        if (this.cursors.left.isDown || this.keys.A.isDown) vx = -speed;
        else if (this.cursors.right.isDown || this.keys.D.isDown) vx = speed;
        if (this.cursors.up.isDown || this.keys.W.isDown) vy = -speed;
        else if (this.cursors.down.isDown || this.keys.S.isDown) vy = speed;
      }

      if (vx && vy) {
        if (Math.abs(vx) >= Math.abs(vy)) {
          vy = 0;
        } else {
          vx = 0;
        }
      }

      this.player.setVelocity(vx, vy);
      const playerDepth = this.getPlayerDepthAt(this.player.x, this.player.y);
      this.player.setDepth(playerDepth);

      if (
        updateLocalOrbAvatar(
          this,
          this.player,
          this.playerOrbImage,
          this.localOrbAvatarData,
          {
            x: this.player.x,
            y: this.player.y,
            vx,
            depth: playerDepth,
          },
        )
      ) {
        return;
      }

      if (Math.abs(vy) > Math.abs(vx)) {
        if (vy < 0) this.player.anims.play("walk-up", true);
        else if (vy > 0) this.player.anims.play("walk-down", true);
      } else if (vx < 0) {
        this.player.anims.play("walk-left", true);
      } else if (vx > 0) {
        this.player.anims.play("walk-right", true);
      } else {
        this.player.anims.stop();
      }
    }
  };
};
